'use client'

import type { Locales } from '@/types/project-types'

import Cookies from 'js-cookie'

import { findSupportedLocale, MAESTRO_LANG_COOKIE } from '@/utils/i18n.utils'
import { reportDiagnostic } from '@maestro/commerce-blocks/client/api/diagnostics'
import { theme } from '@maestro/local/theme'

/**
 * Browser-side counterpart of the request-time locale resolution in
 * `i18n.request.ts`: language cookie, then browser languages, then the site
 * default. app/global-error.tsx renders outside the root layout, where no
 * next-intl request config or provider exists, so it must resolve the locale
 * itself. Never throws: it runs below the last error boundary.
 */
export function resolveClientLocale(): Locales {
  if (typeof window === 'undefined') {
    return theme().site.locale
  }

  try {
    const fromCookie = findSupportedLocale(
      Cookies.get(MAESTRO_LANG_COOKIE) ?? null
    )
    if (fromCookie) {
      return fromCookie
    }

    for (const tag of window.navigator.languages ?? []) {
      const match = findSupportedLocale(tag)
      if (match) {
        return match
      }
    }
  } catch {
    // Cookie or navigator access failed: fall through to the site default.
  }

  return theme().site.locale
}

/**
 * Sends one server-side log line for an error caught by app/global-error.tsx,
 * so it shows up next to the request logs. Production only, fire-and-forget:
 * the error page must render even if reporting fails.
 */
export function reportGlobalError(
  error: Error & { digest?: string },
  pathname: string | null,
  locale: Locales
): void {
  if (typeof window === 'undefined' || process.env.NODE_ENV !== 'production') {
    return
  }

  try {
    void reportDiagnostic({
      level: 'error',
      message: `[GlobalError] ${error?.message ?? 'Unknown error'}`.slice(
        0,
        500
      ),
      context: { digest: error?.digest, pathname, locale },
    }).catch(() => {})
  } catch {
    // Reporting must never break the error page.
  }
}
